import { useTranslation } from "react-i18next";
import { expectedOf, gaugeBands } from "../expected";
import type { DeviceState, PointState } from "../generated/widget-fixture";
import { bandOf, levelEnds, signalBars } from "../glance";
import { Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

const MAX_ROWS = 50;
const ORDER = { offline: 0, sleeping: 1, online: 2 } as const;
const BATTERY = { ok: "text-gray-900", warning: "text-amber-800", critical: "text-red-700" } as const;

type Row = { id: string; d: DeviceState; signal?: PointState; battery?: PointState; bars: number };

/**
 * health: the devices of a source with their last contact, signal bars and battery, worst first (VisionPlatform
 * widget-library-2 WLB-15).
 */
export function Health({ widget, input, now, onOpenDevice }: WidgetProps) {
  const fmt = useWidgetText();
  const { t } = useTranslation();
  const cfg = widget.config ?? {};
  const sk = typeof cfg.signal === "string" ? cfg.signal : "signal";
  const bk = typeof cfg.battery === "string" ? cfg.battery : "battery_percent";
  const num = (p: PointState | undefined) => (typeof p?.latest?.value === "number" ? p.latest.value : undefined);
  const rows: Row[] = (input.members ?? []).flatMap((id) => {
    const d = input.devices?.[id];
    if (!d) return [];
    const signal = input.points?.[key(id, sk)];
    const battery = input.points?.[key(id, bk)];
    return [{ id, d, signal, battery, bars: signalBars(num(signal), d.state === "offline") }];
  });
  rows.sort(
    (a, b) =>
      ORDER[a.d.state] - ORDER[b.d.state] ||
      (num(a.battery) ?? Infinity) - (num(b.battery) ?? Infinity) ||
      a.bars - b.bars ||
      a.d.name.localeCompare(b.d.name, fmt.locale, { numeric: true }),
  );
  const shown = rows.slice(0, MAX_ROWS);
  const offline = rows.some((r) => r.d.state === "offline");
  if (!rows.length) {
    return (
      <Frame widget={widget} state="empty" title={widget.title}>
        <p className="text-gray-700">{fmt.t("list.noDevices")}</p>
      </Frame>
    );
  }
  return (
    <Frame widget={widget} state={offline ? "offline" : "normal"} title={widget.title}>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-700">
              <th className="w-full py-1 font-normal">
                <span className="sr-only">{t("editor.device")}</span>
              </th>
              <th className="py-1 pr-3 font-normal whitespace-nowrap">{fmt.t("health.seen")}</th>
              <th className="py-1 pr-3 font-normal whitespace-nowrap">{fmt.t("health.signal")}</th>
              <th className="py-1 text-right font-normal">{fmt.t("health.battery")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.map((r) => (
              <HealthRow key={r.id} r={r} now={now} onOpen={onOpenDevice} />
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > MAX_ROWS && (
        <p className="text-xs text-gray-700">{fmt.t("health.more", { count: rows.length - MAX_ROWS })}</p>
      )}
    </Frame>
  );
}

function HealthRow({ r, now, onOpen }: { r: Row; now: Date; onOpen?: (id: string) => void }) {
  const fmt = useWidgetText();
  const b = r.battery?.latest?.value;
  const battery = typeof b === "number" && r.battery ? r.battery : undefined;
  const level =
    battery && typeof b === "number"
      ? bandOf(b, gaugeBands(expectedOf(battery), ...levelEnds({}, battery)))
      : undefined;
  const seen = r.d.last_seen_at;
  const dot = r.d.state === "offline" ? "bg-gray-400" : r.d.state === "sleeping" ? "bg-sky-600" : "bg-green-600";
  return (
    <tr>
      <td className="w-full max-w-0 py-1.5 pr-2">
        <span className="flex min-w-0 items-center gap-2">
          <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${dot}`} />
          {onOpen ? (
            <button type="button" onClick={() => onOpen(r.id)} className="truncate text-gray-900 hover:underline">
              {r.d.name}
            </button>
          ) : (
            <span className="truncate text-gray-900">{r.d.name}</span>
          )}
          {r.d.state === "offline" && <span className="shrink-0 text-xs text-amber-900">{fmt.t("state.offline")}</span>}
        </span>
      </td>
      <td className="py-1.5 pr-3 whitespace-nowrap text-gray-700">{seen ? fmt.age(seen, now) : "–"}</td>
      <td className="py-1.5 pr-3">
        <span
          role="img"
          aria-label={fmt.t("health.signalBars", { count: r.bars })}
          className="inline-flex h-3.5 items-end gap-0.5"
        >
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`w-1 rounded-[1px] ${i < r.bars ? "bg-brand" : "bg-gray-200"}`}
              style={{ height: `${4 + i * 3}px` }}
            />
          ))}
        </span>
      </td>
      <td
        className={`py-1.5 text-right font-semibold tabular-nums ${level ? BATTERY[level] : "text-gray-900"}`}
        {...(level ? { "data-band": level } : {})}
      >
        {battery && typeof b === "number" ? fmt.value(b, battery) : "–"}
      </td>
    </tr>
  );
}
